import { yupResolver } from '@hookform/resolvers/yup';
import { Close as CloseIcon } from '@mui/icons-material';
import { Dialog } from '@mui/material';
import { useForm } from 'react-hook-form';
import { debounce } from 'utils/debounce';
import { firstLetterUppercase, removeCpfFormatting } from 'utils/formatters';
import { useStoreSelector } from 'v3/data/store';
import { setRemoveSeller, setSeller } from 'v3/data/store/seller/actions';
import * as api from 'v3/services/api';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { TextButton } from 'v3/shared/components/Button/TextButton';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { TextMaskCustom } from 'v3/shared/components/Inputs/MaskCustomInput';
import { IConfirmationModalProps } from 'v3/shared/components/Modal/ConfirmationModal/types';
import { SellerCodeSchema } from 'v3/shared/schemas/codeSeller';
import { BodySmallRegular } from 'v3/shared/typography/BodySmall';
import { HeadlineSmall } from 'v3/shared/typography/HeadlineSmall';
import { LabelSmall } from 'v3/shared/typography/LabelSmall';
import { theme } from 'v3/styles/global-styles';
import {
  StyledButtonContainer,
  StyledDialogContent,
  StyledIconButton,
  StyledImage,
  TagSeller,
  TagSpaceInputAndCpf,
  confirmationDialogPaperPropsStyle,
} from './styles';

export const SellerCodeModal: React.FC<IConfirmationModalProps> = ({ modal, onClose, onConfirm }) => {
  const {
    seller: { name },
  } = useStoreSelector((store) => store.seller);
  const DEBOUNCE_DELAY = 1000;
  const VERIFY_NUMBER_CODE = 14;
  const { id } = useStoreSelector((store) => store.store.storeLogged);

  const { getValues, control, setError } = useForm<{ code: string }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(SellerCodeSchema) as any,
  });

  const handleCodeChange = async (code: string) => {
    const getCode = getValues('code');

    if (getCode.length === VERIFY_NUMBER_CODE) {
      try {
        const formattedCpf = removeCpfFormatting(code);
        const response = await api.findSeller(formattedCpf, id);
        setSeller(response.data);
      } catch (error) {
        setError('code', { message: 'Consultor não encontrado. Por favor, verifique seu CPF.' });
        console.error('Erro ao buscar consultor:', error);
      }
    }
  };

  const debounceHandleSearchSeller = (code: string) => {
    setRemoveSeller();
    debounce(() => handleCodeChange(code), DEBOUNCE_DELAY, 'handleCodeChange');
  };

  return (
    <Dialog
      open={modal.active}
      onClose={onClose}
      PaperProps={{
        sx: confirmationDialogPaperPropsStyle,
      }}
    >
      <StyledIconButton onClick={onClose}>
        <CloseIcon />
      </StyledIconButton>
      <StyledDialogContent>
        <StyledImage src={modal.image} />
        <HeadlineSmall>{modal.title}</HeadlineSmall>
        <BodySmallRegular>{modal.subtitle}</BodySmallRegular>
        <TagSpaceInputAndCpf>
          <InputFormController
            formControl={control}
            inputType="tel"
            formControlName="code"
            label="Informe seu CPF"
            mask="000.000.000-00"
            placeholder="___.___.___-__"
            customMask={TextMaskCustom}
            fullWidth
            onChangeInput={debounceHandleSearchSeller}
          />
          <TagSeller>
            {name && <LabelSmall color={theme.palette.secondary.light}>Olá, {firstLetterUppercase(name)}</LabelSmall>}
          </TagSeller>
        </TagSpaceInputAndCpf>
        <StyledButtonContainer>
          <FlexButton
            data-testis="flex-button-confirm-button-text"
            size="small"
            text={modal.confirmButtonText}
            handleClick={onConfirm}
            disabled={!name}
          />
          <TextButton
            data-testid="text-button-cancel"
            size="small"
            $outline
            text={modal.cancelButtonText}
            handleClick={onClose}
          />
        </StyledButtonContainer>
      </StyledDialogContent>
    </Dialog>
  );
};
